/**
 * ProjectPicker — disambiguation step for a multi-project .xer upload.
 *
 * When /api/upload returns `{ status: 'multi_project', projects }` (the parser
 * detected >1 PROJECT row and no selected_proj_id was sent), the upload cannot
 * proceed until the user picks ONE project to analyse. This renders that choice;
 * selecting a project re-submits the SAME file with `selected_proj_id` so the
 * normal analyse → PDF → 201 pipeline runs.
 *
 * §24/§25: this is the explicit, non-technical state for a 2xx the uploader
 * cannot act on directly — it must never silently reset to the form (the
 * forensic-confirmed Stage 5.1 bug).
 *
 * Each project carries { projId, projectName, activityCount, dataDate } from
 * the parser's detectProjects() (src/parser/dataBuilder.js). Rendered verbatim
 * — no recompute. CERT-FIX-1 Part 4: the data date is the spec §14 selector's
 * third field (name · activity count · data date) — it is how a planner
 * disambiguates same-named projects; formatted with the same helper the
 * comparison banner uses for data dates (formatDateDDMonYYYY).
 */
import { formatDateDDMonYYYY } from '../../utils/dates.js';

export function ProjectPicker({ projects = [], onSelect, onCancel, busy = false, title = null }) {
  return (
    <div className="project-picker" role="group" aria-label="Choose a project to analyse">
      <h3 className="project-picker-title">
        {title || `This file contains ${projects.length} projects`}
      </h3>
      <p className="project-picker-sub">
        Choose which project to analyse — we’ll build the report for that one.
      </p>
      <ul className="project-picker-list">
        {projects.map((p) => (
          <li key={p.projId} className="project-picker-row">
            <button
              type="button"
              className="project-picker-item"
              onClick={() => onSelect?.(p.projId)}
              disabled={busy}
            >
              <span className="project-picker-name">{p.projectName || p.projId}</span>
              {typeof p.activityCount === 'number' && (
                <span className="project-picker-meta">
                  {p.activityCount.toLocaleString()}{' '}
                  {p.activityCount === 1 ? 'activity' : 'activities'}
                </span>
              )}
              {p.dataDate && (
                <span className="project-picker-meta">
                  Data date {formatDateDDMonYYYY(p.dataDate)}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
      {onCancel && (
        <button
          type="button"
          className="btn-text project-picker-cancel"
          onClick={onCancel}
          disabled={busy}
        >
          Choose a different file
        </button>
      )}
    </div>
  );
}
